import _ from 'lodash';
import moment from 'moment';
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';

// Material UI
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
//import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import Button from '@material-ui/core/Button';

//- Components
import FilterHeader from '../../components/table/FilterHeader';
import EnhancedTableHead from '../../components/table/EnhancedTableHead';

//- Helpers
import { isFiltered, sortTable, PureRecordRow } from '../../libs/tableUtilities';
import { getGlobalClasses } from '../../themes';

//- Containers
import AuthUserContainer from '../../containers/AuthUser';
import TicketCategoryContainer from '../../containers/Ticket/TicketCategory';

const styles = (theme) => {
	return {
		...getGlobalClasses(theme),
		table: {
			width: '100%',
			tableLayout: 'auto',
		},
	};
};

const defaultFleetUsersToCcMap = {
	terminalManagerForFleet: 'Terminal Manager',
	terminalAdminForFleet: 'Terminal Admin',
	mechanicalGroupForFleet: 'Terminal Mechanical Group',
};

const ticketCategoryColumns = [
	{
		id: 'parentChildDisplayName',
		label: 'Parent Category',
	},
	{
		id: 'displayName',
		label: 'Category Name',
	},
	{
		id: 'ticketSchemaDisplayName',
		label: 'Ticket Schema Used',
	},
	{
		id: 'requireDriverReportType',
		label: 'Form Required',
	},
	{
		id: 'defaultAssignedToUserId',
		label: 'Default Assigned To',
	},
	{
		id: 'defaultUserIdsToCcSort',
		label: 'Default CC',
	},
	{
		id: 'dateCreated',
		label: 'Date Created',
	},
	{
		id: 'edit',
		label: 'Edit',
		style: { textAlign: 'center' },
		isNotSortable: true,
	},
];

class TicketCategories extends Component {
	constructor(props) {
		super(props);

		this.state = {
			order: 'asc',
			orderBy: 'parentChildDisplayName',
			filter: '',
			tabIndex: 0,
			filterMap: {},
		};
	}

	componentDidMount() {
		if (!this.props.ticketCategories.length) {
			this.props.searchTicketCategories();
		}
	}

	handleSort = (orderBy) => {
		let order = 'desc';
		if (this.state.orderBy === orderBy && this.state.order === 'desc') {
			order = 'asc';
		}
		this.setState({ order, orderBy });
	};

	handleEdit = (ticketCategoryId) => {
		this.props.history.push(`/ticketCategory/update/${ticketCategoryId}`);
	};

	handleFilter = (event) => {
		this.setState({ filter: event.target.value });
	};

	render() {
		const { classes, ticketCategories, ticketSchemaMap, userMap, permissions } = this.props;

		const { order, orderBy, filter } = this.state;

		if (!permissions || !_.includes(permissions, 'canAddAndUpdateTicketSettings')) {
			return <div>You do not have valid permissions to view this page.</div>;
		}
		if (!ticketCategories) {
			return <div>Fetching ticket categories...</div>;
		}

		//- Only get children
		const ticketCategoryMap = _.keyBy(ticketCategories, 'ticketCategoryId');
		let ticketCategoryRows = [];

		ticketCategories.forEach((ticketCategory) => {
			const {
				parentTicketCategoryIds,
				ticketCategoryId,
				ticketSchemaId,
				displayName,
				defaultAssignedToUserId,
				defaultUserIdsToCc,
				dateCreated,
				assignToType,
				defaultFleetUsersToCc,
				requireDriverReportType,
			} = ticketCategory;

			if (_.isEmpty(parentTicketCategoryIds)) {
				return;
			}

			if (isFiltered({ filter, row: ticketCategory, fields: ['displayName'] })) {
				return;
			}

			const ticketSchema = ticketSchemaMap[ticketSchemaId];
			const ticketSchemaDisplayName = ticketSchema ? ticketSchema.displayName : 'MISSING';

			let defaultAssignedToDisplay = '';
			if (assignToType === 'terminalManagerForFleet') {
				defaultAssignedToDisplay = <i>Terminal Manager</i>;
			} else {
				const user = userMap[defaultAssignedToUserId];
				defaultAssignedToDisplay = user ? `${user.firstName} ${user.lastName}` : 'none';
			}

			let defaultUserIdsToCcSort = '';
			let defaultUserIdsToCcDisplay = '';

			let ccList = [];
			if (!_.isEmpty(defaultUserIdsToCc)) {
				defaultUserIdsToCc.forEach((userId) => {
					const user = userMap[userId] || { firstName: 'Missing' };
					ccList.push({ key: userId, display: `${user.firstName} ${user.lastName}` });
				});
			}

			if (!_.isEmpty(defaultFleetUsersToCc)) {
				defaultUserIdsToCcSort = _.first(defaultUserIdsToCc);
				defaultFleetUsersToCc.forEach((fleetUser) => {
					const fleetUserDisplay = defaultFleetUsersToCcMap[fleetUser] || 'Missing';
					ccList.push({ key: fleetUser, display: <i>{fleetUserDisplay}</i> });
				});
			}

			defaultUserIdsToCcSort = _.first(ccList);
			defaultUserIdsToCcDisplay = ccList.map(({ key, display }) => {
				return <div key={key}>{display}</div>;
			});

			parentTicketCategoryIds.forEach((parentTicketCategoryId) => {
				const parentTicketCategory = ticketCategoryMap[parentTicketCategoryIds];

				if (!parentTicketCategory) {
					console.error(`Missing parent ticket category`, parentTicketCategoryId);
					return;
				}

				const dateCreatedFormatted = moment(dateCreated).format('MMM Do, YYYY');

				const parentDisplayName = parentTicketCategory.displayName;

				ticketCategoryRows.push({
					parentChildDisplayName: `${parentDisplayName}_${displayName}`,
					parentDisplayName,
					ticketCategoryId,
					ticketSchemaDisplayName,
					defaultAssignedToDisplay,
					defaultUserIdsToCcSort,
					defaultUserIdsToCcDisplay,
					displayName,
					defaultAssignedToUserId,
					dateCreated,
					requireDriverReportType: _.startCase(requireDriverReportType),
					dateCreatedFormatted,
					onClick: () => this.handleEdit(ticketCategoryId),
				});
			});
		});

		ticketCategoryRows = sortTable({ rows: ticketCategoryRows, order, orderBy });

		return (
			<Fragment>
				<FilterHeader
					value={this.state.filter}
					onChange={this.handleFilter}
					label={'Ticket Categories'}
					buttonText="Add Ticket Category"
					url="/ticketCategory/add"
					canMakeUpdates={true}
				/>

				<Paper className={classes.globalMainPaper}>
					<Table size="small" className={classes.table}>
						<EnhancedTableHead
							columns={ticketCategoryColumns}
							order={order}
							orderBy={orderBy}
							onRequestSort={this.handleSort}
						/>
						<TableBody>
							{ticketCategoryRows.map((row) => {
								return (
									<PureRecordRow hover className={classes.row} key={row.ticketCategoryId}>
										<TableCell>{row.parentDisplayName}</TableCell>
										<TableCell>{row.displayName}</TableCell>
										<TableCell>{row.ticketSchemaDisplayName}</TableCell>
										<TableCell>{row.requireDriverReportType}</TableCell>
										<TableCell>{row.defaultAssignedToDisplay}</TableCell>
										<TableCell style={{ paddingTop: 3, paddingBottom: 3 }}>
											{row.defaultUserIdsToCcDisplay}
										</TableCell>
										<TableCell>{row.dateCreatedFormatted}</TableCell>
										<TableCell align={'center'} style={{ whiteSpace: 'nowrap' }}>
											<Button onClick={row.onClick}>Edit</Button>
										</TableCell>
									</PureRecordRow>
								);
							})}
						</TableBody>
					</Table>
				</Paper>
			</Fragment>
		);
	}
}

TicketCategories.propTypes = {
	classes: PropTypes.object.isRequired,

	searchTicketCategories: PropTypes.func.isRequired,
	ticketCategories: PropTypes.array,
	ticketSchemaMap: PropTypes.object,
	userMap: PropTypes.object,

	permissions: PropTypes.array,
	order: PropTypes.string,
	orderBy: PropTypes.string,
	history: PropTypes.object,
	theme: PropTypes.object.isRequired,
};

export default _.flow([
	withStyles(styles, { withTheme: true }),
	AuthUserContainer,
	TicketCategoryContainer,
])(TicketCategories);
